import {
  AiSystemsForUserDTO,
  AssignablePlatformRole,
  ChangePlatformRoleDTO,
  GetUsersParams,
  InvitationAcceptanceDTO,
  InvitationAcceptanceRequest,
  InvitationActivationDTO,
  ResendInvitationDTO,
  TeamUsersDTO,
  PlatformInvitationDTO,
  PlatformRole,
  UpdatePlatformRoleRequest,
  UpdateUserRoleRequest,
  UsersListDTO,
  ValidateInvitationResponse
} from '../types/Team.types';
import API from './ApiBase';

export type InviteUserRequest = {
  email: string;
  role: PlatformRole;
  // Organization targeting for an ADMIN invite, mutually exclusive: send either
  // orgName + orgSlug to create a NEW organization, or organizationId to add the
  // ADMIN to an EXISTING one. All are absent for a USER invite (inherits the
  // inviter's org). The API rejects unknown/unexpected fields and rejects mixing
  // organizationId with orgName/orgSlug (ORG_FIELDS_NOT_ALLOWED), so never send
  // more than one of the two modes.
  orgName?: string;
  orgSlug?: string;
  organizationId?: string;
};

export type AssignAiSystemToUserRequest = {
  email: string;
  aiSystemGrants: {
    aiSystemId: string;
    role: string;
  }[];
};

export async function inviteUser(params: InviteUserRequest) {
  return await API.post<PlatformInvitationDTO>(
    '/ai-systems/team/platform-invitations',
    params,
    {
      // The caller shows these errors on the field that caused them, and the
      // documented 403s (ORG_NOT_ACTIVE, USER_NOT_AUTHORIZED) must not throw the
      // user out to the access-denied page mid-invite.
      skipErrorToast: true,
      skipAccessDeniedRedirect: true
    }
  );
}

/**
 * The user directory, scoped server-side from the token: a superadmin sees every
 * organization, an admin their own, a user only their co-assignees. Registered
 * users and pending invitations come back in one paged collection.
 *
 * Replaces the /accounts/{accountId}/team/* trio, which a superadmin could not
 * call at all — they belong to no organization, so there was no id for the path.
 */
export async function getUsers(params: GetUsersParams = {}) {
  const searchParams = new URLSearchParams();

  if (params.q) searchParams.append('q', params.q);
  if (params.type) searchParams.append('type', params.type);
  if (params.status) searchParams.append('status', params.status);
  if (params.platformRole) {
    searchParams.append('platformRole', params.platformRole);
  }
  // Repeated rather than bracketed or comma-joined: the server accepts
  // ?organizationId=a&organizationId=b.
  params.organizationId?.forEach((value) =>
    searchParams.append('organizationId', value)
  );
  if (params.sortBy) searchParams.append('sortBy', params.sortBy);
  if (params.order) searchParams.append('order', params.order);
  if (params.limit !== undefined) {
    searchParams.append('limit', String(params.limit));
  }
  if (params.offset !== undefined) {
    searchParams.append('offset', String(params.offset));
  }

  const queryString = searchParams.toString();

  return await API.get<UsersListDTO>(
    `/users${queryString ? `?${queryString}` : ''}`
  );
}

export async function getTeamUsers(accountId: string) {
  return await API.get<TeamUsersDTO[]>(`/ai-systems/team/users`, {
    params: { accountId }
  });
}

export async function getTeamRoles() {
  return await API.get<unknown>('/ai-systems/team/roles');
}

// The server resolves the invitation's organization from the record, so there is
// no accountId to supply — which is what previously made this uncallable for a
// superadmin and left the Resend button doing nothing.
export async function resendInvitation(invitationId: string) {
  return await API.post<ResendInvitationDTO>(
    `/invitations/${invitationId}/resend`,
    undefined,
    {
      // An admin may not resend an ADMIN-targeted invitation. Show that inline
      // rather than ejecting them to access-denied.
      skipAccessDeniedRedirect: true
    }
  );
}

export async function revokeInvitation(invitationId: string) {
  return await API.delete<{ message: string }>(
    `/invitations/${invitationId}`
  );
}

export async function validateInvitation(token: string) {
  return await API.get<ValidateInvitationResponse>(`/invitations/validate`, {
    params: { token }
  });
}

// OIDC mode: accepts the invitation for an invitee who already has a Keycloak
// identity. Errors are handled by the registration page, not the interceptor.
export async function activateInvitation(token: string) {
  return await API.post<InvitationActivationDTO>(
    `/invitations/activate`,
    { token },
    { skipErrorToast: true, skipAccessDeniedRedirect: true }
  );
}

export async function acceptInvitation(
  bodyRequest: InvitationAcceptanceRequest
) {
  return await API.post<InvitationAcceptanceDTO>(
    `/invitations/accept`,
    bodyRequest
  );
}

// `userId` accepts a user id or an invitation id — pass through whatever the row
// carried. For a USER caller the result is the intersection with their own
// systems, never the target's full list.
export async function getAiSystemsForUser(userId: string, query?: string) {
  return await API.get<AiSystemsForUserDTO>(`/users/${userId}/ai-systems`, {
    params: query ? { q: query } : undefined
  });
}

export async function updateUserRoleInAiSystem(
  userId: string,
  bodyRequest: UpdateUserRoleRequest
) {
  // Only EDITOR and VIEWER are accepted; OWNER moves via the transfer endpoint.
  return await API.patch(`/users/${userId}/roles`, bodyRequest);
}

export async function deleteUserFromSingleAiSystem(
  accountId: string,
  userId: string,
  aiSystemId: string
) {
  return await API.delete(
    `/accounts/${accountId}/team/members/${userId}/ai-systems/${aiSystemId}`
  );
}

/**
 * Revokes a pending invitation. NOT a deletion for a registered member — for
 * one of those the server only unassigns them from their AI systems and leaves
 * them in the organization, still able to sign in. It answers 200 with
 * removedCount 0 rather than 404 when there was nothing to unassign, so calling
 * it for a registered member fails silently. Use deleteUser below for those.
 */
export async function deleteUserFromAccount(invitationId: string) {
  return await API.delete<{ message: string; removedCount: number }>(
    `/users/${invitationId}`
  );
}

/**
 * Really deletes a member: disables the Keycloak identity, anonymizes and
 * deactivates the row, and revokes every invitation they sent.
 *
 * Resolves ids against the User table only, so a pending invitation's id is a
 * 404 here — those keep going through deleteUserFromAccount above.
 */
export async function deleteUser(userId: string) {
  return await API.delete<{ message: string }>(`/admin/users/${userId}`, {
    // Both documented 403s are rules about this target rather than a wrong turn:
    // a superadmin is read-only across organizations, and the server re-reads the
    // caller's persisted role, so an admin demoted elsewhere is refused while
    // their token still says ADMIN. Neither should eject them to access-denied
    // mid-confirmation. The interceptor toast still relays the server's message,
    // which is the error surface for this call.
    skipAccessDeniedRedirect: true
  });
}

/**
 * Promotes a member to organization admin, or demotes one back. No organization
 * is created or destroyed — this moves a role inside one that already exists.
 *
 * Takes effect immediately for everyone except the target, whose existing access
 * token keeps asserting the old role until it expires. The server ends their
 * Keycloak session so their next token carries the new role.
 */
export async function updatePlatformRole(
  userId: string,
  role: AssignablePlatformRole
) {
  const bodyRequest: UpdatePlatformRoleRequest = { role };

  return await API.patch<ChangePlatformRoleDTO>(
    `/users/${userId}/platform-role`,
    bodyRequest,
    {
      // The caller replaces the copy for ADMIN_NOT_FOUND_FOR_OWNERSHIP_TRANSFER,
      // whose server message ("No eligible admin found for ownership transfer")
      // describes an internal invariant an admin cannot act on. Suppressing the
      // toast is what stops it being reported twice.
      skipErrorToast: true,
      // PLATFORM_ROLE_CHANGE_NOT_PERMITTED says who may hold this role, not that
      // the caller is in the wrong place.
      skipAccessDeniedRedirect: true
    }
  );
}

export async function assignAiSystemToUserWithoutInvitation(
  accountId: string,
  params: AssignAiSystemToUserRequest
) {
  return await API.post(`/accounts/${accountId}/team/members/assign`, params, {
    // Assignment is authorized by the caller's role on the target AI system
    // (OWNER), not by their platform role, so a legitimate user can be refused
    // for one system. That must surface as a toast, not eject them to
    // access-denied mid-assignment.
    skipAccessDeniedRedirect: true
  });
}
